import React from 'react';

import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', variant: 'purple' },
  admin: { label: 'Admin/Owner', variant: 'blue' },
  center_manager: { label: 'Center Manager Sales', variant: 'orange' },
  center_manager_ops: { label: 'Center Manager Ops', variant: 'teal' },
  sales: { label: 'Sales', variant: 'green' },
  operations: { label: 'Operations', variant: 'gray' },
};

const TeamUserTable = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onEditUser,
  onManageCenters,
  onChangeRole,
  currentUserRole,
}) => {
  const allSelected = users?.length > 0 && selectedUsers?.length === users?.length;

  const getRoleConfig = (role) => ROLE_CONFIG?.[role] || { label: role, variant: 'gray' };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll?.(e?.target?.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Global Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Center Memberships</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map((user) => {
              const roleConfig = getRoleConfig(user?.role);
              const memberships = user?.centerMemberships || [];
              return (
                <tr key={user?.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers?.includes(user?.id)}
                      onChange={(e) => onSelectUser?.(user?.id, e?.target?.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-blue-600">
                          {user?.fullName?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user?.fullName || '—'}</div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <Badge variant={roleConfig?.variant}>{roleConfig?.label}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    {memberships?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {memberships?.slice(0, 2)?.map((m) => (
                          <span
                            key={m?.centerId || m?.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
                          >
                            {m?.centerName || m?.centers?.name || 'Center'}
                          </span>
                        ))}
                        {memberships?.length > 2 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            +{memberships?.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No centers assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{user?.phone || '—'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {user?.isActive ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="danger">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{formatDate(user?.createdAt)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEditUser?.(user)}
                        title="Edit user details"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <Icon name="Edit" size={15} />
                      </button>
                      <button
                        onClick={() => onChangeRole?.(user)}
                        title="Change role"
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        <Icon name="Shield" size={15} />
                      </button>
                      <button
                        onClick={() => onManageCenters?.(user)}
                        title="Manage center memberships"
                        className="p-1.5 rounded hover:bg-indigo-50 text-indigo-500 hover:text-indigo-700 transition-colors"
                      >
                        <Icon name="Building" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {users?.length === 0 && (
        <div className="text-center py-16">
          <Icon name="Users" size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">No users found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default TeamUserTable;
